import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiHome,
  FiMail,
  FiMapPin,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiToggleLeft,
  FiToggleRight,
  FiUsers,
  FiX,
  FiXCircle,
} from 'react-icons/fi';
import PageLayout from '../components/layout/PageLayout';
import { getAllHosts } from '../store/thunks/HostActions';
import { deleteHostReset } from '../store/slices/HostSlices';
import request from '../utils/request';

const numberFormatter = new Intl.NumberFormat('en-US');
const STALE_ORDER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const HOST_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'no-properties', label: 'No properties' },
];

const SORT_OPTIONS = [
  { id: 'active', label: 'Active first' },
  { id: 'newest', label: 'Newest signup' },
  { id: 'oldest', label: 'Oldest signup' },
  { id: 'properties', label: 'Most properties' },
  { id: 'name', label: 'Name A-Z' },
  { id: 'lastOrder', label: 'Last order' },
];

const normalizeText = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9@.\-\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const digitsOnly = (value = '') => String(value).replace(/\D/g, '');

export const splitTerms = (value) => normalizeText(value).split(' ').filter(Boolean);

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

const getHostName = (host) => {
  const fullName = `${host?.firstName || ''} ${host?.lastName || ''}`.trim();
  return fullName || host?.companyName || host?.email || 'Unnamed host';
};

const getInitials = (host) => {
  const initials = getHostName(host)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return initials || 'H';
};

const isActiveHost = (host) => Boolean(host?.isVerified);

const isGuestConciergeEnabled = (host) => host?.guestConciergeServicesEnabled !== false;

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const toTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

// Backend joins per-property search strings with " | ". Older payloads used a
// single space, in which case we fall back to one combined string.
export const splitPropertyText = (value = '') =>
  String(value)
    .split(' | ')
    .map((part) => part.trim())
    .filter(Boolean);

// Word-start matches rank above plain substring matches so "ext" ranks
// "Extraordinary Stays" above "Next Door Rentals".
const matchWeight = (haystack, term, { prefix, contains }) => {
  if (!haystack || !term) return 0;
  if (haystack.startsWith(term) || haystack.includes(` ${term}`)) return prefix;
  if (haystack.includes(term)) return contains;
  return 0;
};

/**
 * Scores a host against every search term. Every term must hit at least one
 * field; the score is the sum of the best field hit per term. Returns null
 * when a term matches nothing.
 */
export const scoreHost = (host, terms) => {
  if (!terms.length) return { score: 0, matchedProperties: [] };

  let score = 0;
  const matchedProperties = new Set();

  for (const term of terms) {
    const termDigits = digitsOnly(term);
    let best = 0;

    best = Math.max(best, matchWeight(host.nameText, term, { prefix: 8, contains: 5 }));
    best = Math.max(best, matchWeight(host.companyText, term, { prefix: 7, contains: 4 }));
    best = Math.max(best, matchWeight(host.emailText, term, { prefix: 4, contains: 3 }));

    if (termDigits.length >= 3 && host.phoneDigits.includes(termDigits)) {
      best = Math.max(best, 4);
    }

    let propertyBest = 0;
    for (const property of host.propertyIndex) {
      const weight = matchWeight(property.text, term, { prefix: 3, contains: 2 });
      if (weight) {
        matchedProperties.add(property.label);
        propertyBest = Math.max(propertyBest, weight);
      }
    }
    best = Math.max(best, propertyBest);

    if (!best) return null;
    score += best;
  }

  return { score, matchedProperties: Array.from(matchedProperties) };
};

function KpiTile({ label, value, sublabel, tone = 'zinc', active = false, onClick }) {
  const toneText = {
    zinc: 'text-zinc-900',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    sky: 'text-sky-700',
  };
  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={joinClasses(
        'relative flex h-[60px] w-full flex-col justify-center rounded-xl border bg-white px-3 text-left transition-colors duration-150',
        active ? 'border-zinc-900' : 'border-zinc-200',
        onClick ? 'hover:border-zinc-300' : ''
      )}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="mt-0.5 flex items-baseline justify-between gap-2">
        <span className={joinClasses('text-[17px] font-semibold leading-none tracking-tight tabular-nums', toneText[tone] || toneText.zinc)}>
          {value}
        </span>
        {sublabel ? <span className="truncate text-[10.5px] text-zinc-500">{sublabel}</span> : null}
      </div>
      {onClick ? (
        <span
          aria-hidden
          className={joinClasses(
            'absolute inset-x-3 bottom-0 h-[2px] origin-left rounded-t-full bg-zinc-900 transition-transform duration-200',
            active ? 'scale-x-100' : 'scale-x-0'
          )}
        />
      ) : null}
    </Component>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={joinClasses(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1',
        active
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-amber-50 text-amber-700 ring-amber-200'
      )}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function SecondaryBadge({ label, tone = 'zinc' }) {
  const classes = {
    zinc: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    sky: 'bg-sky-50 text-sky-700 ring-sky-200',
  };

  return (
    <span className={joinClasses('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1', classes[tone] || classes.zinc)}>
      {label}
    </span>
  );
}

function InlineBanner({ tone = 'error', children, onClose }) {
  const styles = {
    error: 'border-red-200 bg-red-50 text-red-700',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  };

  return (
    <div className={joinClasses('mb-3 flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm', styles[tone] || styles.error)}>
      <div className="flex items-start gap-2">
        {tone === 'success' ? <FiCheckCircle className="mt-0.5 h-4 w-4" /> : <FiAlertCircle className="mt-0.5 h-4 w-4" />}
        <div>{children}</div>
      </div>
      {onClose ? (
        <button type="button" onClick={onClose} className="opacity-70 transition hover:opacity-100">
          <FiXCircle className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

export default function HostListScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, hostList, error } = useSelector((state) => state.allHosts);
  const {
    message: deleteMessage,
    success: deleteSuccess,
    error: deleteError,
  } = useSelector((state) => state.deleteHost);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('active');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedHostIds, setSelectedHostIds] = useState(new Set());
  const [bulkConciergeSaving, setBulkConciergeSaving] = useState(false);
  const [togglingHostId, setTogglingHostId] = useState(null);
  const [localNotice, setLocalNotice] = useState(null);

  const deferredSearch = useDeferredValue(search);
  const searchTerms = useMemo(() => splitTerms(deferredSearch), [deferredSearch]);
  const isSearching = searchTerms.length > 0;

  const loadHosts = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      await dispatch(getAllHosts({ limit: 1000, status: 'all' })).unwrap();
    } catch (_error) {
      // Slice state drives the error banner for this screen.
    } finally {
      if (silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!deleteSuccess && !deleteError) return undefined;
    const timeoutId = setTimeout(() => {
      dispatch(deleteHostReset());
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [deleteError, deleteSuccess, dispatch]);

  const hosts = useMemo(() => {
    if (!Array.isArray(hostList)) return [];

    return hostList.map((host) => {
      const active = isActiveHost(host);
      const propertyCount = Number(host?.propertyCount || 0);
      const company = host?.companyName?.trim() || '';
      const phone = host?.phoneNumber?.trim() || '';
      const displayName = getHostName(host);

      return {
        ...host,
        active,
        propertyCount,
        guestConciergeServicesEnabled: isGuestConciergeEnabled(host),
        company,
        phone,
        lastOrderAt: host?.lastOrderAt || null,
        createdAtTime: toTime(host?.createdAt),
        lastOrderTime: toTime(host?.lastOrderAt),
        displayName,
        nameText: normalizeText(displayName),
        companyText: normalizeText(company),
        emailText: normalizeText(host?.email),
        phoneDigits: digitsOnly(phone),
        propertyIndex: splitPropertyText(host?.propertySearchText).map((label) => ({
          label,
          text: normalizeText(label),
        })),
      };
    });
  }, [hostList]);

  const filterCounts = useMemo(() => {
    return hosts.reduce(
      (acc, host) => {
        acc.all += 1;
        if (host.active) acc.active += 1;
        if (!host.active) acc.inactive += 1;
        if (!host.propertyCount) acc['no-properties'] += 1;
        return acc;
      },
      {
        all: 0,
        active: 0,
        inactive: 0,
        'no-properties': 0,
      }
    );
  }, [hosts]);

  const filteredHosts = useMemo(() => {
    const matched = [];

    for (const host of hosts) {
      if (filterStatus === 'active' && !host.active) continue;
      if (filterStatus === 'inactive' && host.active) continue;
      if (filterStatus === 'no-properties' && host.propertyCount > 0) continue;

      const result = scoreHost(host, searchTerms);
      if (!result) continue;

      matched.push({ ...host, score: result.score, matchedProperties: result.matchedProperties });
    }

    const byActive = (left, right) => {
      if (right.active !== left.active) return Number(right.active) - Number(left.active);
      if (right.propertyCount !== left.propertyCount) return right.propertyCount - left.propertyCount;
      return left.displayName.localeCompare(right.displayName);
    };

    const comparators = {
      active: isSearching
        ? (left, right) => right.score - left.score || byActive(left, right)
        : byActive,
      newest: (left, right) => right.createdAtTime - left.createdAtTime || byActive(left, right),
      oldest: (left, right) => left.createdAtTime - right.createdAtTime || byActive(left, right),
      properties: (left, right) => right.propertyCount - left.propertyCount || byActive(left, right),
      name: (left, right) => left.displayName.localeCompare(right.displayName),
      lastOrder: (left, right) => right.lastOrderTime - left.lastOrderTime || byActive(left, right),
    };

    return matched.sort(comparators[sortBy] || byActive);
  }, [filterStatus, hosts, isSearching, searchTerms, sortBy]);

  const totalProperties = useMemo(
    () => hosts.reduce((sum, host) => sum + host.propertyCount, 0),
    [hosts]
  );

  const verifiedCount = useMemo(
    () => hosts.filter((host) => host.isVerified).length,
    [hosts]
  );

  const displayedProperties = useMemo(
    () => filteredHosts.reduce((sum, host) => sum + host.propertyCount, 0),
    [filteredHosts]
  );

  const dormantHostCount = useMemo(() => {
    const staleCutoff = Date.now() - STALE_ORDER_WINDOW_MS;

    return hosts.filter((host) => {
      if (!host.lastOrderAt) return false;
      const lastOrderTime = new Date(host.lastOrderAt).getTime();
      return Number.isFinite(lastOrderTime) && lastOrderTime < staleCutoff;
    }).length;
  }, [hosts]);

  const hostsWithoutOrders = useMemo(
    () => hosts.filter((host) => !host.lastOrderAt).length,
    [hosts]
  );

  const guestConciergeEnabledCount = useMemo(
    () => hosts.filter(isGuestConciergeEnabled).length,
    [hosts]
  );

  const allVisibleSelected = filteredHosts.length > 0 && filteredHosts.every((host) => selectedHostIds.has(host._id));
  const hasActiveFilters = Boolean(search) || filterStatus !== 'all' || sortBy !== 'active';

  const resetFilters = () => {
    setSearch('');
    setFilterStatus('all');
    setSortBy('active');
  };

  const toggleVisibleSelection = () => {
    setSelectedHostIds((previous) => {
      const next = new Set(previous);
      if (allVisibleSelected) {
        filteredHosts.forEach((host) => next.delete(host._id));
      } else {
        filteredHosts.forEach((host) => next.add(host._id));
      }
      return next;
    });
  };

  const toggleHostSelection = (hostId) => {
    setSelectedHostIds((previous) => {
      const next = new Set(previous);
      if (next.has(hostId)) next.delete(hostId);
      else next.add(hostId);
      return next;
    });
  };

  const applyGuestConciergeBulk = async (enabled) => {
    const hostIds = Array.from(selectedHostIds);
    if (!hostIds.length) return;

    setBulkConciergeSaving(true);
    setLocalNotice(null);
    try {
      await request.patch('/admin/hosts/bulk-update', {
        hostIds,
        updates: { guestConciergeServicesEnabled: enabled },
      });
      setSelectedHostIds(new Set());
      setLocalNotice({
        tone: 'success',
        message: `Guest concierge turned ${enabled ? 'on' : 'off'} for ${numberFormatter.format(hostIds.length)} host${hostIds.length === 1 ? '' : 's'}.`,
      });
      await loadHosts({ silent: true });
    } catch (_error) {
      setLocalNotice({
        tone: 'error',
        message: 'Failed to update guest concierge visibility. Refresh and try again.',
      });
    } finally {
      setBulkConciergeSaving(false);
    }
  };

  const toggleGuestConciergeForHost = async (host, event) => {
    event.stopPropagation();
    const enabled = !isGuestConciergeEnabled(host);

    setTogglingHostId(host._id);
    setLocalNotice(null);
    try {
      await request.patch(`/admin/hosts/${host._id}`, {
        guestConciergeServicesEnabled: enabled,
      });
      setLocalNotice({
        tone: 'success',
        message: `Guest concierge turned ${enabled ? 'on' : 'off'} for ${host.displayName}.`,
      });
      await loadHosts({ silent: true });
    } catch (_error) {
      setLocalNotice({
        tone: 'error',
        message: 'Failed to update this host. Refresh and try again.',
      });
    } finally {
      setTogglingHostId(null);
    }
  };

  const errorText = typeof error === 'string' && error.trim() ? error : null;

  return (
    <PageLayout
      title="Property Managers"
      subtitle="Manage host accounts and property access at a glance."
      showSearch={false}
      fullWidth
      actions={(
        <button
          type="button"
          onClick={() => loadHosts({ silent: true })}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiRefreshCw className={joinClasses('h-4 w-4', refreshing ? 'animate-spin' : '')} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    >
      {deleteSuccess && deleteMessage ? (
        <InlineBanner tone="success" onClose={() => dispatch(deleteHostReset())}>
          {deleteMessage}
        </InlineBanner>
      ) : null}

      {deleteError ? (
        <InlineBanner tone="error" onClose={() => dispatch(deleteHostReset())}>
          {deleteError}
        </InlineBanner>
      ) : null}

      {localNotice ? (
        <InlineBanner tone={localNotice.tone} onClose={() => setLocalNotice(null)}>
          {localNotice.message}
        </InlineBanner>
      ) : null}

      {error ? (
        <InlineBanner tone="error">
          <span className="font-semibold">Failed to load Property Managers.</span>{' '}
          {errorText ? <span className="opacity-80">{errorText}</span> : 'Refresh and try again.'}
        </InlineBanner>
      ) : null}

      <div className="space-y-3">
        <section className="grid grid-cols-3 gap-2 laptop:grid-cols-6">
          <KpiTile
            label="Total hosts"
            value={numberFormatter.format(filterCounts.all)}
            sublabel={`${numberFormatter.format(totalProperties)} properties`}
            active={filterStatus === 'all'}
            onClick={() => setFilterStatus('all')}
          />
          <KpiTile
            label="Active"
            value={numberFormatter.format(filterCounts.active)}
            sublabel="verified"
            tone="emerald"
            active={filterStatus === 'active'}
            onClick={() => setFilterStatus('active')}
          />
          <KpiTile
            label="Inactive"
            value={numberFormatter.format(filterCounts.inactive)}
            sublabel="needs onboarding"
            tone="amber"
            active={filterStatus === 'inactive'}
            onClick={() => setFilterStatus('inactive')}
          />
          <KpiTile
            label="Visible properties"
            value={numberFormatter.format(displayedProperties)}
            sublabel={`${numberFormatter.format(verifiedCount)} verified hosts`}
            tone="sky"
          />
          <KpiTile
            label="Guest concierge on"
            value={numberFormatter.format(guestConciergeEnabledCount)}
            sublabel={`${numberFormatter.format(filterCounts.all - guestConciergeEnabledCount)} off`}
            tone="emerald"
          />
          <KpiTile
            label="Dormant 30d+"
            value={numberFormatter.format(dormantHostCount)}
            sublabel={`${numberFormatter.format(hostsWithoutOrders)} no orders`}
            tone="amber"
          />
        </section>

        <section className="rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, company, email, phone, or any property address (e.g. 725 Whiting)"
              aria-label="Search property managers"
              className="h-10 w-full rounded-xl border border-zinc-200 bg-zinc-50 pl-10 pr-10 text-sm text-zinc-900 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700"
              >
                <FiX className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {HOST_FILTERS.map((filter) => {
              const active = filter.id === filterStatus;
              return (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setFilterStatus(filter.id)}
                  className={joinClasses(
                    'inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition',
                    active
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:text-zinc-900'
                  )}
                >
                  <span>{filter.label}</span>
                  <span className={joinClasses('rounded-md px-1.5 py-0.5 text-[10px] tabular-nums', active ? 'bg-white/15 text-white' : 'bg-zinc-100 text-zinc-500')}>
                    {numberFormatter.format(filterCounts[filter.id] || 0)}
                  </span>
                </button>
              );
            })}

            <label className="relative inline-flex h-8 items-center">
              <span className="pointer-events-none absolute left-2.5 text-xs text-zinc-400">Sort</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                aria-label="Sort hosts"
                className={joinClasses(
                  'h-8 appearance-none rounded-lg border bg-white pl-10 pr-7 text-xs font-medium text-zinc-900 outline-none transition hover:bg-zinc-50 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100',
                  sortBy !== 'active' ? 'border-zinc-900' : 'border-zinc-200'
                )}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-zinc-400" />
            </label>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50"
              >
                <FiX className="h-3.5 w-3.5 text-zinc-400" />
                Reset
              </button>
            ) : null}

            <span className="ml-auto text-xs text-zinc-500">
              <span className="font-semibold tabular-nums text-zinc-900">{numberFormatter.format(filteredHosts.length)}</span> hosts
              {filteredHosts.length !== filterCounts.all ? (
                <> · <span className="font-semibold tabular-nums text-zinc-900">{numberFormatter.format(displayedProperties)}</span> properties</>
              ) : null}
              {isSearching && sortBy === 'active' ? <span className="text-zinc-400"> · ranked by match</span> : null}
            </span>
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-2.5">
            <label className="inline-flex h-8 items-center gap-2 text-xs font-medium text-zinc-700">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleVisibleSelection}
                className="rounded border-zinc-300"
              />
              Select visible
            </label>
            <span className="text-xs tabular-nums text-zinc-500">
              {numberFormatter.format(selectedHostIds.size)} selected
            </span>
            <span className="hidden text-xs text-zinc-400 laptop:inline">Guest concierge:</span>
            <button
              type="button"
              onClick={() => applyGuestConciergeBulk(true)}
              disabled={!selectedHostIds.size || bulkConciergeSaving}
              className="h-8 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Turn on
            </button>
            <button
              type="button"
              onClick={() => applyGuestConciergeBulk(false)}
              disabled={!selectedHostIds.size || bulkConciergeSaving}
              className="h-8 rounded-lg border border-zinc-300 bg-white px-3 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Turn off
            </button>
            {selectedHostIds.size ? (
              <button
                type="button"
                onClick={() => setSelectedHostIds(new Set())}
                className="ml-auto text-xs font-medium text-zinc-500 transition hover:text-zinc-800"
              >
                Clear selection
              </button>
            ) : null}
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
          {loading ? (
            <div className="space-y-3 p-4">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="animate-pulse rounded-xl border border-zinc-200 p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-xl bg-zinc-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-48 rounded bg-zinc-200" />
                      <div className="h-3 w-72 rounded bg-zinc-100" />
                    </div>
                    <div className="h-8 w-24 rounded-full bg-zinc-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredHosts.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400">
                <FiUsers className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-zinc-900">No hosts match the current view</h3>
                <p className="mt-1 text-sm text-zinc-500">
                  {isSearching
                    ? `Nothing matched "${deferredSearch.trim()}" by name, contact, or property address.`
                    : 'Try a different filter to return to the full list.'}
                </p>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {filteredHosts.map((host) => {
                const guestConciergeEnabled = isGuestConciergeEnabled(host);
                const selected = selectedHostIds.has(host._id);
                const ToggleIcon = guestConciergeEnabled ? FiToggleRight : FiToggleLeft;
                const matchedProperties = host.matchedProperties || [];

                return (
                <div
                  key={host._id}
                  onClick={() => navigate(`/hostList/${host._id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/hostList/${host._id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className="group flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition hover:bg-zinc-50"
                >
                  <div
                    className="mt-3"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleHostSelection(host._id)}
                      aria-label={`Select ${host.displayName}`}
                      className="rounded border-zinc-300"
                    />
                  </div>

                  <div className={joinClasses(
                    'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold',
                    host.active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  )}>
                    {getInitials(host)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-[15px] font-semibold text-zinc-900">{host.displayName}</p>
                      {host.company && host.company !== host.displayName ? (
                        <span className="truncate text-sm text-zinc-500">{host.company}</span>
                      ) : null}
                      <StatusBadge active={host.active} />
                      <SecondaryBadge label={host.isOnBoarded ? 'Onboarded' : 'Needs onboarding'} tone={host.isOnBoarded ? 'sky' : 'amber'} />
                      {!host.propertyCount ? <SecondaryBadge label="No properties" tone="zinc" /> : null}
                    </div>

                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-zinc-500">
                      {host.email ? (
                        <span className="inline-flex items-center gap-1.5">
                          <FiMail className="h-3.5 w-3.5" />
                          {host.email}
                        </span>
                      ) : null}
                      {host.phone ? (
                        <span className="inline-flex items-center gap-1.5">
                          <FiPhone className="h-3.5 w-3.5" />
                          {host.phone}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1.5">
                        <FiHome className="h-3.5 w-3.5" />
                        {numberFormatter.format(host.propertyCount)} {host.propertyCount === 1 ? 'property' : 'properties'}
                      </span>
                      <span className="text-xs text-zinc-400">Signed up {formatDate(host.createdAt)}</span>
                      {host.lastOrderAt ? (
                        <span className="text-xs text-zinc-400">Last order {formatDate(host.lastOrderAt)}</span>
                      ) : null}
                    </div>

                    {matchedProperties.length ? (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-indigo-700">
                        <FiMapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">Matched property:</span>
                        <span className="truncate">{matchedProperties.slice(0, 2).join(' · ')}</span>
                        {matchedProperties.length > 2 ? (
                          <span className="text-indigo-500">+{matchedProperties.length - 2} more</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-1 flex shrink-0 items-center gap-2 text-sm font-medium text-zinc-400 transition group-hover:text-zinc-700">
                    <button
                      type="button"
                      onClick={(event) => toggleGuestConciergeForHost(host, event)}
                      onKeyDown={(event) => event.stopPropagation()}
                      disabled={togglingHostId === host._id}
                      title={`Guest concierge ${guestConciergeEnabled ? 'on' : 'off'}`}
                      className={joinClasses(
                        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
                        guestConciergeEnabled
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100'
                          : 'bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-200'
                      )}
                    >
                      <ToggleIcon className="h-4 w-4" />
                      {guestConciergeEnabled ? 'Concierge on' : 'Concierge off'}
                    </button>
                    <FiChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </PageLayout>
  );
}
